import { initializeApp } from 'firebase/app';
import {
  getFirestore, doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove,
  collection, getDocs, onSnapshot, addDoc,
  serverTimestamp, query, orderBy, deleteField
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyAELQvSWSmBt9RcL-d1eUlT2oogIbiQVkY",
  authDomain: "vm-tipping-2026.firebaseapp.com",
  projectId: "vm-tipping-2026",
  storageBucket: "vm-tipping-2026.firebasestorage.app",
  messagingSenderId: "1060307731188",
  appId: "1:1060307731188:web:df1771b481b630bd6cd4a1",
  measurementId: "G-5WE2HWBY2Q"
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);

// ── Users ────────────────────────────────────────────────────────────
export async function getUser(username) {
  const snap = await getDoc(doc(db, 'users', username));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getAllUsers() {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createUser(username, data) {
  await setDoc(doc(db, 'users', username), data);
}

export async function updateUser(username, data) {
  await updateDoc(doc(db, 'users', username), data);
}

// ── Results (admin sets these) ───────────────────────────────────────
export async function getResults() {
  const snap = await getDoc(doc(db, 'config', 'results'));
  return snap.exists() ? snap.data() : {};
}

export async function setResults(data) {
  await setDoc(doc(db, 'config', 'results'), data, { merge: true });
}

// Fjerner et enkelt kampresultat HELT (deleteField) – merge:true kan ikke
// fjerne eksisterende felt, kun overskrive/legge til, så dette må gjøres separat.
// Brukes til å nullstille feilregistrerte kamper (poeng + bracket-fremrykning forsvinner automatisk).
export async function clearMatchResult(matchId) {
  await updateDoc(doc(db, 'config', 'results'), { [matchId]: deleteField() });
}

// ── Phase ────────────────────────────────────────────────────────────
export async function getPhase() {
  const snap = await getDoc(doc(db, 'config', 'phase'));
  return snap.exists() ? snap.data().value : 'pre';
}

export async function setPhase(value) {
  await setDoc(doc(db, 'config', 'phase'), { value });
}

// ── Card stats ───────────────────────────────────────────────────────
export async function getCardStats() {
  const snap = await getDoc(doc(db, 'config', 'cards'));
  return snap.exists() ? snap.data() : {};
}

export async function setCardStats(data) {
  await setDoc(doc(db, 'config', 'cards'), data, { merge: true });
}

// ── Chat (realtime listener) ─────────────────────────────────────────
export function subscribeChatMessages(callback) {
  const q = query(collection(db, 'chat'), orderBy('ts', 'asc'));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function sendChatMessage(user, text, image='') {
  await addDoc(collection(db, 'chat'), {
    user, text, image: image||'', ts: serverTimestamp()
  });
}

// ── Phase realtime listener ──────────────────────────────────────────
export function subscribePhase(callback) {
  return onSnapshot(doc(db, 'config', 'phase'), snap => {
    callback(snap.exists() ? snap.data().value : 'pre');
  });
}

// ── Results realtime listener ────────────────────────────────────────
export function subscribeResults(callback) {
  return onSnapshot(doc(db, 'config', 'results'), snap => {
    callback(snap.exists() ? snap.data() : {});
  });
}

export async function updatePresence(username, displayName) {
  await setDoc(doc(db, 'presence', username), { ts: Date.now(), displayName: displayName || username });
}

export async function deleteChatMessage(id) {
  const { deleteDoc, doc: fdoc } = await import('firebase/firestore');
  await deleteDoc(fdoc(db, 'chat', id));
}

export function subscribeOnlineUsers(callback) {
  return onSnapshot(collection(db, 'presence'), snap => {
    const now = Date.now();
    const active = snap.docs
      .filter(d => now - (d.data().ts || 0) < 60000)
      .map(d => ({ displayName: d.data().displayName || d.id, ts: d.data().ts || 0 }))
      .sort((a, b) => b.ts - a.ts);
    callback(active);
  });
}

// ── Live-hendelse fra Cloud Function ────────────────────────────────
export function subscribeLiveEvent(callback) {
  return onSnapshot(doc(db, 'config', 'liveEvent'), snap => {
    callback(snap.exists() ? snap.data() : null);
  });
}

// ── Quiz-spiller fra Firestore (roteres kl. 06:00 av Cloud Function) ─
export function subscribeQuizPlayer(callback) {
  return onSnapshot(doc(db, 'config', 'quizPlayer'), snap => {
    callback(snap.exists() ? snap.data().idx : null);
  });
}

// ── Stats-cache (toppscorere og kort fra Cloud Function) ─────────────
export function subscribeStatsCache(callback) {
  return onSnapshot(doc(db, 'config', 'statsCache'), snap => {
    callback(snap.exists() ? snap.data() : null);
  });
}

// ── Chat reactions ───────────────────────────────────────────────────
export async function addReaction(msgId, emoji, username) {
  const ref = doc(db, 'chat', msgId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const reactions = snap.data().reactions || {};
  const users = reactions[emoji] || [];
  const alreadyReacted = users.includes(username);
  await updateDoc(ref, {
    [`reactions.${emoji}`]: alreadyReacted ? arrayRemove(username) : arrayUnion(username)
  });
}

// ── Toppscorere (bygget fra mål-events) ─────────────────────────────
export function subscribeScorers(callback) {
  return onSnapshot(doc(db, 'config', 'scorers'), snap => {
    if (!snap.exists()) { callback([]); return; }
    const data = snap.data();
    const list = Object.values(data).filter(s => typeof s === 'object' && s.name);
    callback(list.sort((a, b) => b.goals - a.goals));
  });
}

// ── Kortstatistikk (live) ────────────────────────────────────────────
export function subscribeCardStats(callback) {
  return onSnapshot(doc(db, 'config', 'cards'), snap => {
    callback(snap.exists() ? snap.data() : {});
  });
}

// ── Vinnerpodiet (admin-styrt visning for alle besøkende) ───────────
export function subscribePodium(callback) {
  return onSnapshot(doc(db, 'config', 'podium'), snap => {
    callback(snap.exists() ? snap.data() : { active: false });
  });
}

export async function setPodiumActive(active) {
  await setDoc(doc(db, 'config', 'podium'), { active, activatedAt: Date.now() }, { merge: true });
}
